import React, { useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";

const API = process.env.REACT_APP_BACKEND_URL || "";

/**
 * DszOrderDecidePage — Iter 191
 *
 * Landing page a buyer arrives at from the OOS decision email. They can
 * either **backorder** (we wait for restock and ship then) or
 * **cancel & refund**. Works both logged-in (session cookie) and
 * logged-out (via signed `t` token from the email link).
 */
export default function DszOrderDecidePage() {
  const { orderId } = useParams();
  const [params] = useSearchParams();
  const token = params.get("t") || "";
  const navigate = useNavigate();

  const [state, setState] = useState({ loading: true, error: "", data: null });
  const [submitting, setSubmitting] = useState("");
  const [note, setNote] = useState("");
  const [result, setResult] = useState(null);

  useEffect(() => {
    const url = `${API}/api/dsz-orders/${encodeURIComponent(orderId)}/oos${
      token ? `?t=${encodeURIComponent(token)}` : ""
    }`;
    fetch(url, { credentials: "include" })
      .then(async (r) => {
        if (!r.ok) {
          const t = await r.text().catch(() => "");
          throw new Error(t || `HTTP ${r.status}`);
        }
        return r.json();
      })
      .then((data) => setState({ loading: false, error: "", data }))
      .catch((e) =>
        setState({ loading: false, error: String(e.message || e), data: null }),
      );
  }, [orderId, token]);

  const submit = async (choice) => {
    setSubmitting(choice);
    try {
      const url = `${API}/api/dsz-orders/${encodeURIComponent(orderId)}/decide${
        token ? `?t=${encodeURIComponent(token)}` : ""
      }`;
      const res = await fetch(url, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ choice, note: note || null }),
      });
      const body = await res.json();
      if (!res.ok || !body?.ok) throw new Error(body?.detail || "failed");
      setResult(body);
    } catch (e) {
      setState((s) => ({ ...s, error: String(e.message || e) }));
    } finally {
      setSubmitting("");
    }
  };

  if (state.loading) {
    return (
      <div
        data-testid="dsz-decide-loading"
        className="min-h-screen flex items-center justify-center text-slate-500"
      >
        Loading order details…
      </div>
    );
  }
  if (state.error) {
    return (
      <div
        data-testid="dsz-decide-error"
        className="min-h-screen flex items-center justify-center p-6"
      >
        <div className="max-w-md w-full bg-white border border-rose-200 rounded-xl p-8 shadow-sm">
          <h1 className="text-xl font-semibold text-rose-700 mb-2">
            We couldn&apos;t verify your access
          </h1>
          <p className="text-sm text-slate-600 mb-4">
            The link may have expired or your session is signed out. Please
            re-open the link from your email, or sign in and try again.
          </p><button
            data-testid="dsz-decide-signin-btn"
            onClick={() => navigate("/login")}
            className="px-4 py-2 rounded-lg bg-slate-900 text-white text-sm"
          >
            Sign in
          </button>
        </div>
      </div>
    );
  }

  const { data } = state;
  const alreadyDecided = data?.decision;

  if (result || alreadyDecided) {
    const choice = result?.choice || alreadyDecided;
    return (
      <div
        data-testid="dsz-decide-done"
        className="min-h-screen flex items-center justify-center p-6 bg-slate-50"
      >
        <div className="max-w-md w-full bg-white border border-emerald-200 rounded-2xl p-8 shadow">
          <h1 className="text-2xl font-semibold text-emerald-700 mb-2">
            {choice === "cancel"
              ? "Refund on its way"
              : "Backorder confirmed"}
          </h1>
          <p className="text-sm text-slate-600 mb-4">
            {choice === "cancel"
              ? "Your refund has been queued to the original payment method. Card refunds take 5–10 business days."
              : "We'll ship your items as soon as the warehouse restocks. No extra charge."}
          </p>
          {result?.refund && result.refund.ok === false && (
            <p className="text-xs text-amber-700 mb-3">
              Note: our team will process this refund manually (ref:{" "}
              {String(result.refund.ref || "queued").slice(0, 80)}).
            </p>
          )}
          <button
            data-testid="dsz-decide-keep-shopping-btn"
            onClick={() => navigate("/plaza")}
            className="w-full px-4 py-2 rounded-lg bg-slate-900 text-white text-sm"
          >
            Keep browsing HustlePlaza →
          </button>
        </div>
      </div>
    );
  }

  return (
    <div
      data-testid="dsz-decide-page"
      className="min-h-screen bg-slate-50 py-10 px-4"
    >
      <div className="max-w-xl mx-auto bg-white border border-slate-200 rounded-2xl p-8 shadow-sm">
        <div className="text-xs uppercase tracking-wide text-slate-500 mb-2">
          Order #{orderId}
        </div>
        <h1 className="text-2xl font-semibold text-slate-900 mb-2">
          A few items are out of stock
        </h1>
        <p className="text-sm text-slate-600 mb-4">
          We flagged this while confirming with the warehouse. Choose what
          you&apos;d like us to do — either option keeps a shopping link on file.
        </p>
        <ul
          data-testid="dsz-decide-oos-list"
          className="divide-y divide-slate-100 border border-slate-100 rounded-lg mb-6"
        >
          {(data?.oos_items || []).map((it) => (
            <li
              key={it.sku}
              className="flex items-center justify-between px-3 py-2 text-sm"
            >
              <span className="font-mono text-slate-700">{it.sku}</span>
              <span className="text-amber-700">
                you asked for {it.requested}, only {it.available} available
              </span>
            </li>
          ))}
        </ul>

        <label className="block text-xs uppercase tracking-wide text-slate-500 mb-1">
          Optional note (kept on file)
        </label>
        <textarea
          data-testid="dsz-decide-note"
          value={note}
          onChange={(e) => setNote(e.target.value)}
          rows={2}
          maxLength={500}
          placeholder="e.g. please refund, I'll re-order the in-stock size"
          className="w-full text-sm border border-slate-200 rounded-lg px-3 py-2 mb-6 focus:outline-none focus:ring-2 focus:ring-slate-300"
        />

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <button
            data-testid="dsz-decide-backorder-btn"
            disabled={!!submitting}
            onClick={() => submit("backorder")}
            className="px-4 py-3 rounded-lg bg-slate-900 text-white text-sm font-medium disabled:opacity-50"
          >
            {submitting === "backorder" ? "Saving…" : "Backorder — wait for restock"}
          </button>
          <button
            data-testid="dsz-decide-cancel-btn"
            disabled={!!submitting}
            onClick={() => submit("cancel")}
            className="px-4 py-3 rounded-lg border border-rose-300 text-rose-700 text-sm font-medium disabled:opacity-50"
          >
            {submitting === "cancel" ? "Refunding…" : "Cancel & refund"}
          </button>
        </div>

        <p className="text-xs text-slate-500 mt-6">
          Emailed to {data?.buyer_email_masked || "you"} · we keep every
          decision on file with a timestamp.
        </p>
      </div>
    </div>
  );
}
